import React, { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Alert, Result } from "antd";
import CustomButton from "Components/CustomButton";
import Spin from "Components/Spinner";
import { LoadingOutlined, ReloadOutlined } from "@ant-design/icons";
import { formatCurrency, formatMBReference } from "utils/helper";
import { payment_mb_post } from "api/payments";
import { order_post } from "api/orders";
import SelectPaymentMethodModal from "../../../Shared/SelectPaymentMethodModal";
import OfflinePaymentInfoAlert from "../../../Shared/OfflinePaymentInfoAlert";

import { utmParams } from "utils/helper";
const { utm_source, utm_medium, utm_campaign, utm_term, utm_content } = utmParams();
const env = process.env?.ENVIRONMENT?.toLowerCase() || "development";

/***********************************************************************************************
 ***********************************************************************************************
 ***********************************************************************************************
 * ##: APP
 ***********************************************************************************************
 ***********************************************************************************************
 **********************************************************************************************/
const Multibanco = ({ productData, data, actionHandler, step, customStyle }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [selectPaymentMethod, setSelectPaymentMethod] = useState({ visible: false });
  const [waitForPayment, setWaitForPayment] = useState(false);
  const [paymentDataResponse, setPaymentDataResponse] = useState({});
  const [errorFetching, setErrorFetching] = useState(false);
  const [paid, setPaid] = useState(false);
  const status = data?.status?.action;

  useEffect(() => {
    if (status === "prepare-payment") getMBReference();
  }, []);

  useEffect(() => {
    const status = data?.status?.action;
    if (status === "paid") {
      setPaid(true);
    }
  }, [data]);

  const createOrder = (payload) => {
    return new Promise((resolve) => {
      try {
        order_post(payload)
          .then((res) => resolve({ status: true, data: res }))
          .catch((err) => resolve({ status: false, data: err }));
      } catch (error) {
        resolve({ status: false, data: error });
      }
    });
  };

  const getMBReference = async () => {
    if (data?.identification?.email === undefined || data?.identification?.email === null) {
      return;
    }
    setLoading(true);
    const payload = {
      buyer: data.identification,
      products: data.purchase_products,
      purchase_id: data.purchase_id,
      main_product: data.main_product,
      coupon: data.coupon,
      pathname: window.location.pathname,
      origin: window.location.origin,
      affiliate_id: data.affiliate_id,
      utm_source,
      utm_medium,
      utm_campaign,
      utm_term,
      utm_content,
      free_resolution_waiver: data.identification?.free_resolution_checkbox || false,
      is_offer: data.identification?.is_offer || false,
      offer_info: {
        gift_name: data.identification?.gift_name || "",
        gift_email: data.identification?.gift_email || "",
        gift_message: data.identification?.gift_message || "",
        gift_delivery_date: data.identification?.gift_delivery_date || "",
      },
    };

    // Disable hability to change quantity
    actionHandler("qty_locked", true);

    const order = await createOrder({ ...payload, method: "mb", source: "checkout" });
    if (!order.status) {
      setLoading(false);
      setErrorFetching(true);
      return;
    }

    payment_mb_post(payload)
      .then((response) => {
        setPaymentDataResponse({
          entity: response.data.entity,
          reference: response.data.reference,
          value: response.data.value,
        });

        if (env !== "production") {
          console.log("Multibanco Provider", response.data.provider);
        }

        setErrorFetching(false);
        setWaitForPayment(true);
        actionHandler("update_status", {
          action: "wait-for-payment",
          data: {
            method_id: "MB",
            entity: response.data.entity,
            reference: response.data.reference,
            value: response.data.value,
            purchase_id: response.data.purchase_id,
            payment_unique_id: response.data.payment_unique_id,
          },
        });
      })
      .catch((error) => {
        //console.log("error", error);
        setErrorFetching(true);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  //console.log(data?.purchase_products, purchaseProductsTotal(data?.purchase_products));

  return (
    <div className="multibanco-container">
      <div className="payment-data-container">
        {paid === false && (
          <Spin spinning={loading} tip={t("multibanco.loadingTip")}>
            {loading && <div className="loading"></div>}

            {errorFetching && !loading && (
              <div className="error">
                <Alert
                  message={t("multibanco.fetchErrorTitle")}
                  description={t("multibanco.fetchErrorDescription")}
                  type="error"
                  showIcon
                />
                <div style={{ textAlign: "center" }}>
                  <CustomButton buttonStyle={customStyle} type="primary" onClick={() => getMBReference()}>
                    {t("multibanco.retryButton")} <ReloadOutlined />
                  </CustomButton>
                </div>
                <div style={{ textAlign: "center" }}>
                  <CustomButton buttonStyle={customStyle} type="link" onClick={() => setSelectPaymentMethod({ visible: true })}>
                    {t("multibanco.changeMethodButton")}
                  </CustomButton>
                </div>
              </div>
            )}
            {waitForPayment && !errorFetching && (
              <>
                <div className="payment-data">
                  <div className="payment-text">
                    <Trans i18nKey="multibanco.entity" values={{ value: paymentDataResponse?.entity }} components={{ strong: <strong /> }} />
                  </div>
                  <div className="payment-text">
                    <Trans i18nKey="multibanco.reference" values={{ value: formatMBReference(paymentDataResponse?.reference) }} components={{ strong: <strong /> }} />
                  </div>
                  <div className="payment-text">
                    <Trans i18nKey="multibanco.amount" values={{ value: formatCurrency(paymentDataResponse?.value) }} components={{ strong: <strong /> }} />
                  </div>
                </div>
                <div className="payment-instructions">{t("multibanco.paymentInstructions")}</div>
              </>
            )}

            {waitForPayment && !errorFetching && (
              <div className="payment-loading">
                <Alert
                  message={
                    <div className="payment-loading-alert">
                      <LoadingOutlined /> {t("multibanco.awaitingPayment")}
                    </div>
                  }
                  type="warning"
                />
                <OfflinePaymentInfoAlert />
              </div>
            )}
          </Spin>
        )}
      </div>
      {paid === true && <Result status="success" title={t("multibanco.paidTitle")} subTitle={t("multibanco.paidSubtitle")} />}

      <SelectPaymentMethodModal
        data={data}
        actionHandler={actionHandler}
        visible={selectPaymentMethod.visible}
        close={() => setSelectPaymentMethod({ visible: false })}
        customStyle={customStyle}
      />
    </div>
  );
};

export default Multibanco;
